import { all, select } from 'redux-saga/effects';
import uniq from 'lodash/uniq';

import { type GeneratorResult, selectTyped } from '@pressreader/appstore';
import { ArticleFields, type IArticle, type IArticleUpdatable } from '@pressreader/content-types';
import { getKnownMissingArticleFields } from '@pressreader/content-utils';
import { hasFlag } from '@pressreader/utils';

import type { ArticleIdsForSocialMetaOps } from '../../types';
import { getArticleIdForSocialMetadataOps } from '../../utils';
import { selectArticleById, selectArticleByIdAndLanguage } from '../selectors';

import { loadArticleById, loadArticlesById } from './load.article';

type SocialMetaDefaultValueType = never;
type GetArticleSocialMetadataResultComments<T extends IArticleUpdatable> = T extends { comments: { count: infer C } } & {
    comments: { count: number };
}
    ? { commentsCount: C }
    : { commentsCount: SocialMetaDefaultValueType };
type GetArticleSocialMetadataResultVotes<T extends IArticleUpdatable> = T extends { votes: { likes: infer L; dislikes: infer D } }
    ? { upVotes: L extends number ? L : SocialMetaDefaultValueType; downVotes: D extends number ? D : SocialMetaDefaultValueType }
    : { upVotes: SocialMetaDefaultValueType; downVotes: SocialMetaDefaultValueType };

type GetArticleSocialMetadataResult<T extends IArticleUpdatable> = GetArticleSocialMetadataResultComments<T> & GetArticleSocialMetadataResultVotes<T>;

export function getArticleSocialMetadata<T extends IArticleUpdatable>(articleUpdate: T): GetArticleSocialMetadataResult<T> {
    const articleCommentsUpdate = articleUpdate.comments;
    const articleVotesUpdate = articleUpdate.votes;
    const socialMetadataUpdate = {} as GetArticleSocialMetadataResult<T>;

    if (Number.isInteger(articleCommentsUpdate?.count)) {
        socialMetadataUpdate.commentsCount = articleCommentsUpdate?.count as GetArticleSocialMetadataResultComments<T>['commentsCount'];
    }
    if (Number.isInteger(articleVotesUpdate?.likes)) {
        socialMetadataUpdate.upVotes = articleVotesUpdate?.likes as GetArticleSocialMetadataResultVotes<T>['upVotes'];
    }
    if (Number.isInteger(articleVotesUpdate?.dislikes)) {
        socialMetadataUpdate.downVotes = articleVotesUpdate?.dislikes as GetArticleSocialMetadataResultVotes<T>['downVotes'];
    }

    return socialMetadataUpdate;
}

export function* articleFieldsToLoadToCompleteSocialMetadata(
    articleId: string,
    language: IArticle['language'] | null,
): GeneratorResult<ArticleFields> {
    const articleData = yield* selectTyped(selectArticleByIdAndLanguage, {
        articleId,
        language,
    });
    const knownMissingFields = articleData ? getKnownMissingArticleFields(articleData) : ArticleFields.None;

    const articleFieldTypesToLoad: ArticleFields[] = [];
    if (!articleData || (!articleData?.comments && !hasFlag(articleData.availableFields, ArticleFields.Comments))) {
        articleFieldTypesToLoad.push(ArticleFields.Comments);
    }
    if (!articleData || (!articleData?.votes && !hasFlag(articleData.availableFields, ArticleFields.Votes))) {
        articleFieldTypesToLoad.push(ArticleFields.Votes);
    }

    if (articleFieldTypesToLoad.length === 0) {
        return ArticleFields.None;
    }

    let requiredFields: ArticleFields = articleFieldTypesToLoad.reduce((acc, curr) => acc | curr, ArticleFields.None);
    if (knownMissingFields !== ArticleFields.None) {
        requiredFields &= ~knownMissingFields;
    }
    return requiredFields;
}

export function* loadSocialMetadataForArticle({
    articleIdsForSocialMeta,
    loadedFields,
    language,
}: {
    articleIdsForSocialMeta: ArticleIdsForSocialMetaOps;
    language: IArticle['language'] | null;
    loadedFields?: ArticleFields;
}) {
    const articleIdToLoad = getArticleIdForSocialMetadataOps(articleIdsForSocialMeta);

    let requiredFields = yield* articleFieldsToLoadToCompleteSocialMetadata(articleIdToLoad, language);
    if (loadedFields) {
        requiredFields &= ~loadedFields;
    }

    if (requiredFields === ArticleFields.None) {
        return;
    }
    yield* loadArticleById(articleIdToLoad, { requiredFields, lang: language });
}

export function* loadArticlesSocialMetadata({
    articleIdsForSocialMeta: articlesIdentities,
}: {
    articleIdsForSocialMeta: ArticleIdsForSocialMetaOps[];
}) {
    const articleIdsForFetchingMetadata = articlesIdentities.map(articleIdsForSocialMeta => ({
        articleId: getArticleIdForSocialMetadataOps(articleIdsForSocialMeta),
        language: articleIdsForSocialMeta.language,
    }));
    const articleIdsToLoad: Array<string | null> = yield all(
        articleIdsForFetchingMetadata.map(function* filterArticlesToLoad({ articleId, language }) {
            const fieldsToLoad = yield* articleFieldsToLoadToCompleteSocialMetadata(articleId, language ?? null);
            return fieldsToLoad === ArticleFields.None ? null : articleId;
        }),
    );
    const articleIdsToLoadFilteredFromNulls: string[] = uniq(articleIdsToLoad.filter((articleId): articleId is string => articleId !== null));

    yield* loadArticlesById({
        articleIds: articleIdsToLoadFilteredFromNulls,
        fields: ArticleFields.Comments | ArticleFields.Votes,
    });
}

/**
 * Reads from store primary article ids for the given article ids.
 * If primary article id is not available for the article, it will be fetched
 * from the server.
 * If the primary article id is not available for the article,
 * the value will be "undefined".
 */
export function* loadArticlePrimaryIds(articleIds: string[]): GeneratorResult<
    Array<{
        articleId: string;
        /**
         * Primary article id for the article.
         * If the article is primary the value equals to the articleId.
         *
         * "undefined" is invalid value, meaning that primaryArticleId can not be
         * figured out for the article.
         */
        primaryArticleId: string | undefined;
    }>
> {
    const articlesMetadataFromStore: Record<string, ReturnType<typeof selectArticleById>> = yield all(
        articleIds.reduce((result, articleId: string) => {
            result[articleId] = select(selectArticleById, { articleId });
            return result;
        }, {} as Record<string, ReturnType<typeof select>>),
    );
    const articlesWithoutPrimaryId: string[] = [];
    const articlesPrimaryIdMap = Object.keys(articlesMetadataFromStore).reduce((result, articleId) => {
        const articleData = articlesMetadataFromStore[articleId];
        const primaryArticleId = articleData?.primaryArticleId;

        if (!primaryArticleId) {
            articlesWithoutPrimaryId.push(articleId);
        } else {
            result[articleId] = {
                articleId,
                primaryArticleId,
            };
        }
        return result;
    }, {} as Record<string, { articleId: string; primaryArticleId: string | undefined }>);

    const articlesMetadataFetched = yield* loadArticlesById({
        articleIds: articlesWithoutPrimaryId,
        fields: ArticleFields.None,
    });

    articlesMetadataFetched?.forEach(({ id: articleId, primaryArticleId }) => {
        articlesPrimaryIdMap[articleId] = {
            articleId,
            primaryArticleId,
        };
    });
    return Object.values(articlesPrimaryIdMap);
}
